"use client";

import { useEffect, useRef } from "react";
import { APIProvider, Map, ColorScheme, AdvancedMarker } from "@vis.gl/react-google-maps";
import { X, Navigation } from "lucide-react";

interface VenueMapPopupProps {
  venueName: string;
  address: string;
  latitude: number;
  longitude: number;
  onClose: () => void;
}

export function VenueMapPopup({
  venueName,
  address,
  latitude,
  longitude,
  onClose,
}: VenueMapPopupProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
  const mapId = process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID;

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Close on backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  };

  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;

  if (!apiKey) return null;

  return (
    <div
      ref={overlayRef}
      onClick={handleBackdropClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
    >
      <div className="relative w-full max-w-lg overflow-hidden rounded-lg border border-neutral-800 bg-surface shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-neutral-800 px-4 py-3">
          <div className="min-w-0">
            <h3 className="truncate font-mono text-xs font-bold tracking-wider text-white">
              {venueName}
            </h3>
            <p className="truncate font-mono text-[11px] text-neutral-500">
              {address}
            </p>
          </div>
          <div className="flex items-center gap-2 ml-3 shrink-0">
            <a
              href={directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 rounded-md border border-neutral-700 bg-neutral-800 px-2.5 py-1.5 font-mono text-[10px] tracking-wider text-neutral-300 transition-colors hover:border-acid hover:text-acid"
            >
              <Navigation className="h-3 w-3" />
              DIRECTIONS
            </a>
            <button
              onClick={onClose}
              className="flex h-7 w-7 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-800 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Map */}
        <div className="h-[300px] w-full sm:h-[350px]">
          <APIProvider apiKey={apiKey}>
            <Map
              defaultCenter={{ lat: latitude, lng: longitude }}
              defaultZoom={16}
              mapId={mapId || undefined}
              colorScheme={ColorScheme.DARK}
              gestureHandling="greedy"
              disableDefaultUI={true}
              clickableIcons={false}
              className="h-full w-full"
            >
              <AdvancedMarker position={{ lat: latitude, lng: longitude }}>
                <div className="relative flex items-center justify-center">
                  <div className="h-4 w-4 rounded-full border-2 border-acid bg-acid/20 shadow-[0_0_12px_#D1F805]" />
                  <div className="absolute h-8 w-8 animate-ping rounded-full bg-acid/10" />
                </div>
              </AdvancedMarker>
            </Map>
          </APIProvider>
        </div>
      </div>
    </div>
  );
}
